<template>
  <div class="reservation-form relative">
    <h1 class="capitalize px-sm text-aba-blue">Make Reservation</h1>
    <div class="px-sm mt-base mb-xl">
      <h1 class="">{{eventData.eventInfo.title}}</h1>
      <div>{{eventData.eventInfo.secondTittle}}</div>
      <div class="desc">
        <div>{{eventData.eventInfo.dateString}}</div>
        <div>{{eventData.eventInfo.locationString}}</div>
      </div>
    </div>
    <smooth-reflow>
      <template v-if="!response">
        <px-input ref="name" v-model="name" label="Your Name" @blur="validateForm"/>
        <px-input ref="email" v-model="email" label="Email" required @blur="validateForm"/>
      </template>
      <div v-else class="flex flex-col items-center justify-center bg-milk p-sm">
        <div v-html="response"/>
        <a v-if="showRetryButton" href="#" @click.prevent="retry">Try another email address</a>
      </div>
    </smooth-reflow>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { db, functions } from '../../../lib/firebase'
import PxInput from '../UI/inputs/PxInput'
import SmoothReflow from '../UI/SmoothReflow'

export default {
  name: 'ReservationForm',
  components: { SmoothReflow, PxInput },
  props: {
    eventData: { type: Object, required: true },
    formValidator: { type: Function }
  },

  data: () => ({
    name: '',
    email: '',
    deliveryState: '',
    unsubscribeMail: null,
    emailDoc: null,
    error: null,
    timeoutId: null,
    mailDocCheckTimeout: null
  }),

  computed: {
    ...mapState(['user']),
    response () {
      if (this.error) {
        const errorStr = this.error.toString()
        if (errorStr.includes('Reservation already confirmed')) {
          return `<h1 class="mt-base mb-xxl">Reservation for ${this.email}<br>has been already confirmed.</h1>`
        }
        if (errorStr.includes('already exists')) {
          return `<h1 class="mt-base mb-xxl">Reservation already exists</h1><p class="text-red-700 mt-base">A reservation with this email already exists for this event.</p>`
        }
        if (errorStr.includes('backend issue') || errorStr.includes('email service did not respond')) {
          return `<h1 class="mt-base mb-xxl text-red-700">Email Service Issue</h1><p class="text-red-700 mt-base">${errorStr.replace(/^Error:\s*/, '')}</p><p class="mt-base">Your reservation was created, but we couldn't send the confirmation email. Please contact support with your email address: <strong>${this.email}</strong></p>`
        }
        // Extract error message (remove "Error: " prefix if present)
        const errorMessage = errorStr.replace(/^Error:\s*/, '')
        return `<p class="text-red-700">${errorMessage}</p>`
      }
      switch (this.deliveryState) {
        case 'ERROR':
          return `<p class="text-red-700">Sorry, there was an error during email delivery to the address: ${this.email}.</p>`
        case 'SUCCESS':
          return `${this.name ? this.name + ', ' : ''}<h1 class="text-aba-blue my-sm">Please, confirm your reservation!</h1>
<p>We have sent you an Email with the confirmation link.</p>
<div class="desc mt-base">In case you don't see the email in your <span class="italic font-medium">Inbox</span>,<br>
please also check your <span class="italic font-medium">Spam/Junk</span> folder.</div>`
        case 'PENDING':
        case 'PROCESSING':
        default:
          return ''
      }
    },
    showRetryButton () {
      return this.error || this.deliveryState === 'ERROR'
    }
  },

  created () {
    this.$emit('update-buttons', undefined)
  },

  beforeDestroy () {
    this.unsubscribeMailDoc()
  },

  methods: {
    validateForm () {
      let valid = true
      if (this.$refs.email) {
        valid = this.$refs.email.isValid
      }
      this.$emit('valid', valid)
      return valid
    },

    async submit () {
      if (!this.validateForm()) return
      const reservationData = {
        ...this.eventData,
        email: this.email,
        name: this.name
      }
      try {
        this.$emit('processing', true)
        this.$emit('update-processing-message', 'Creating reservation...')
        console.log('Submitting reservation with data:', {
          email: this.email,
          name: this.name,
          eventId: this.eventData.eventId
        })
        // Use the correct function name (matches the exported function name)
        const makeReservation = functions.httpsCallable('reservationMake', {
          timeout: 60000 // 60 second timeout
        })
        console.log('Calling Firebase function: reservationMake')
        const result = await makeReservation(reservationData)
        console.log('Firebase function response:', result)
        const reservationId = result.data

        if (!reservationId) {
          throw new Error('No reservation ID returned from server')
        }

        console.log('Reservation created with ID:', reservationId)
        this.$emit('update-processing-message', 'Sending Email...')

        // Set up a check to see if mail document is created within 5 seconds
        this.mailDocCheckTimeout = setTimeout(() => {
          db.collection('mail')
            .doc(reservationId)
            .get()
            .then(doc => {
              if (!doc.exists) {
                console.error('Mail document was not created after 5 seconds. Reservation ID:', reservationId)
                console.error('This indicates the backend Firebase Function is not creating the mail document.')
                // Show a warning message to the user
                this.$emit('update-processing-message', 'Email service is not responding. Your reservation may have been created, but the confirmation email could not be sent.')
              } else {
                console.log('Mail document exists:', doc.data())
              }
            })
            .catch(err => {
              console.error('Error checking mail document:', err)
            })
        }, 5000)

        // Set up timeout (60 seconds - email sending can take time)
        this.timeoutId = setTimeout(() => {
          if (this.mailDocCheckTimeout) {
            clearTimeout(this.mailDocCheckTimeout)
            this.mailDocCheckTimeout = null
          }
          if (this.unsubscribeMail) {
            this.unsubscribeMail()
            this.unsubscribeMail = null
          }
          if (this.deliveryState !== 'SUCCESS' && this.deliveryState !== 'ERROR') {
            // Check if document exists but just doesn't have delivery info yet
            if (this.emailDoc && !this.emailDoc.delivery) {
              this.error = new Error('Email is being prepared. Please check your email inbox in a few minutes. The confirmation email may still arrive.')
            } else if (!this.emailDoc) {
              // Mail document was never created - this is a backend issue
              console.error('Timeout: Mail document was never created. This indicates the backend Firebase Function is not working correctly.')
              this.error = new Error('The email service did not respond. Your reservation was created, but the confirmation email could not be sent. This appears to be a backend issue. Please contact support with your email address and reservation details.')
            } else {
              this.error = new Error('Email sending is taking longer than expected. Please check your email inbox or spam folder, or try again later.')
            }
            this.deliveryState = 'ERROR'
            this.$emit('processing', false)
            this.$emit('update-buttons', { close: 'OK' })
          }
        }, 60000)

        this.unsubscribeMail = db.collection('mail')
          .doc(reservationId)
          .onSnapshot(doc => {
            if (!doc.exists) {
              console.warn('Mail document does not exist yet. Reservation ID:', reservationId)
              // Check again after a delay
              setTimeout(() => {
                db.collection('mail')
                  .doc(reservationId)
                  .get()
                  .then(checkDoc => {
                    if (!checkDoc.exists) {
                      console.error('Mail document still does not exist after delay. This indicates a backend issue.')
                      console.error('The Firebase Function "reservation-make" is not creating the mail document.')
                      console.error('Please check the backend Firebase Functions code.')
                      // Update processing message to inform user
                      this.$emit('update-processing-message', 'Email service error. Your reservation was created, but the confirmation email could not be sent. Please contact support.')
                    }
                  })
                  .catch(err => {
                    console.error('Error checking mail document existence:', err)
                  })
              }, 10000)
              return
            }

            this.emailDoc = doc.data()
            console.log('Mail document updated:', this.emailDoc)

            if ((this.emailDoc || {}).delivery) {
              this.deliveryState = this.emailDoc.delivery.state
              console.log('Delivery state:', this.deliveryState)

              if (this.deliveryState === 'SUCCESS' || this.deliveryState === 'ERROR') {
                if (this.timeoutId) {
                  clearTimeout(this.timeoutId)
                  this.timeoutId = null
                }
                this.error = this.emailDoc.delivery.error || null
                this.$emit('processing', false)
                this.$emit('update-buttons', { close: this.error ? 'Cancel' : 'OK' })
                if (this.unsubscribeMail) {
                  this.unsubscribeMail()
                  this.unsubscribeMail = null
                }
              } else {
                // Update processing message based on state
                const stateMessages = {
                  'PENDING': 'Preparing email...',
                  'PROCESSING': 'Sending email...'
                }
                this.$emit('update-processing-message', stateMessages[this.deliveryState] || 'Sending Email...')
              }
            } else {
              console.log('Mail document exists but no delivery field yet. Document:', this.emailDoc)
              // Document exists but no delivery field - might still be processing
              // Check if it's been more than 10 seconds, show a message
              const createdAt = this.emailDoc.createdAt
              const createdAtMillis = createdAt && typeof createdAt.toMillis === 'function' ? createdAt.toMillis() : Date.now()
              const docAge = Date.now() - createdAtMillis
              if (docAge > 10000) {
                this.$emit('update-processing-message', 'Email is being processed. This may take a moment...')
              } else {
                this.$emit('update-processing-message', 'Preparing email...')
              }
            }
          }, error => {
            if (this.timeoutId) {
              clearTimeout(this.timeoutId)
              this.timeoutId = null
            }
            console.error('Mail document listener error:', error)
            this.error = error
            this.deliveryState = 'ERROR'
            this.$emit('processing', false)
            this.$emit('update-buttons', { close: 'Cancel' })
            if (this.unsubscribeMail) {
              this.unsubscribeMail()
              this.unsubscribeMail = null
            }
          })
      } catch (error) {
        // Clean up any timeouts
        if (this.timeoutId) {
          clearTimeout(this.timeoutId)
          this.timeoutId = null
        }
        if (this.mailDocCheckTimeout) {
          clearTimeout(this.mailDocCheckTimeout)
          this.mailDocCheckTimeout = null
        }
        console.error('Reservation submission error:', error)
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          stack: error.stack
        })

        // Extract error details from Firebase function error
        let errorMessage = 'An error occurred while processing your reservation.'

        // Parse the error message first to understand the context
        const msg = error.message || ''

        // Check for "already-exists" code - this typically means duplicate reservation
        if (error.code === 'already-exists') {
          // If the message mentions email delivery, it might be a secondary issue
          if (msg.includes('Email delivery error') || msg.includes('email delivery')) {
            // Reservation exists, but there was also an email issue
            // The backend is reporting both issues - likely the reservation exists AND email failed
            errorMessage = 'A reservation with this email already exists for this event. Additionally, there was an issue with the email service. Please check your spam folder or contact support if you need assistance.'
          } else {
            // Standard duplicate reservation
            errorMessage = 'A reservation with this email already exists for this event.'
          }
        } else if (error.code === 'failed-precondition') {
          // Failed precondition could mean various things
          if (msg.includes('Unauthorized') || msg.includes('unauthorized') || msg.includes('permission')) {
            errorMessage = 'Unauthorized access. The reservation service is not properly configured. Please contact support.'
          } else if (msg.includes('already') || msg.includes('exists') || msg.includes('duplicate')) {
            errorMessage = 'A reservation with this email already exists for this event.'
          } else if (msg.includes('Email delivery error') || msg.includes('email delivery')) {
            errorMessage = 'There was an issue sending the confirmation email. The email service may be temporarily unavailable. Your reservation may still have been created - please check your email inbox or contact support.'
          } else {
            // Show the actual error message if available, otherwise use generic message
            const detailedMsg = msg || error.details || 'Unknown error'
            errorMessage = `The reservation could not be processed: ${detailedMsg}. Please try again or contact support.`
            // Log full error details for debugging
            console.error('Failed-precondition error details:', {
              message: error.message,
              details: error.details,
              code: error.code,
              fullError: error
            })
          }
        } else if (error.code === 'permission-denied') {
          errorMessage = 'You do not have permission to make a reservation.'
        } else if (error.code === 'invalid-argument') {
          errorMessage = 'Invalid reservation data. Please check your information and try again.'
        } else if (error.code === 'unavailable') {
          errorMessage = 'The reservation service is temporarily unavailable. Please try again later.'
        } else if (msg) {
          // Handle "Email delivery error" cases
          if (msg.includes('Email delivery error') || msg.includes('email delivery')) {
            errorMessage = 'There was an issue sending the confirmation email. The email service may be temporarily unavailable. Your reservation may still have been created - please check your email inbox or contact support.'
          } else if (msg.includes('already') || msg.includes('exists') || msg.includes('duplicate')) {
            errorMessage = 'A reservation with this email already exists for this event.'
          } else if (msg.includes('limit') || msg.includes('full') || msg.includes('capacity')) {
            errorMessage = 'This event has reached its reservation limit.'
          } else {
            // Use the error message as-is, but clean it up
            errorMessage = msg.replace(/^Error:\s*/, '').replace(/undefined/g, 'an issue').replace(/:\s*undefined/g, '')
          }
        }

        this.error = new Error(errorMessage)
        this.deliveryState = 'ERROR'
        // Clear processing state and message immediately
        this.$emit('update-processing-message', '')
        this.$emit('processing', false)
        this.$emit('update-buttons', { close: 'OK' })
        if (this.unsubscribeMail) {
          this.unsubscribeMail()
          this.unsubscribeMail = null
        }
      }
    },

    retry () {
      this.unsubscribeMailDoc()
      this.error = null
      this.email = ''
      this.$emit('update-buttons', { close: 'Cancel', submit: 'Submit' })
      this.$nextTick(() => {
        if (this.$refs.email) this.$refs.email.focus()
      })
    },

    unsubscribeMailDoc () {
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
        this.timeoutId = null
      }
      if (this.mailDocCheckTimeout) {
        clearTimeout(this.mailDocCheckTimeout)
        this.mailDocCheckTimeout = null
      }
      if (typeof this.unsubscribeMail === 'function') {
        this.unsubscribeMail()
      }
      this.unsubscribeMail = null
      this.emailDoc = null
      this.deliveryState = ''
    }
  }
}
</script>

<style lang="scss">
  .reservation-form {
    margin-top: 0 !important;
  }
</style>
